import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import GroupList from '../../MyComponents/GroupCard/GroupList';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import GenericDeletionConfirmation from '../../MyComponents/GenericDeletionConfirmation/GenericDeletionConfirmation';
import EditIcon from '../../MyComponents/icons/EditIcon';
import ShareLessonIcon from '../../MyComponents/icons/ShareLessonIcon';
import DeleteIcon from '../../MyComponents/icons/DeleteIcon';
import loadClassesHook from '../../MyComponents/Hooks/loadClassesHook';
import CreateGroupForm from './CreateGroupForm';
import classApi from '../../Api/classApi';
import store from '../../redux/store';
import { shareLesson, showMultiBoardLessonState, userJoinRequest } from '../../redux/actions';
import { GA_EVENT_TREE } from '../../gaConstants';
import { tutorialLinks } from '../../common/urlLinks';
import { CARD, TEXT_LINK, cx } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '760px',
        marginBottom: '28px',
    },
    eyebrowRow: {
        marginBottom: '18px',
    },
    title: {
        margin: '0 0 12px',
        fontSize: 'clamp(1.9rem, 3.6vw, 2.8rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '620px',
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '1.04rem',
        lineHeight: 1.6,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px 24px',
        marginBottom: '22px',
    },
    count: {
        margin: 0,
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px 20px',
    },
    // The "all / mine" filter, on the pricing section's segmented toggle.
    track: {
        display: 'inline-flex',
        padding: '4px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
    },
    toggle: {
        minHeight: '36px',
        padding: '0 16px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.88rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        cursor: 'pointer',
    },
    toggleOn: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 3px rgba(6, 63, 69, 0.12)',
    },
    howItWorks: TEXT_LINK,
    empty: {
        ...CARD,
        padding: 'clamp(24px, 3vw, 36px)',
        background: REDESIGN_COLORS.mist,
        borderColor: REDESIGN_COLORS.primaryBorderSoft,
    },
    emptyTitle: {
        margin: '0 0 8px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    emptyText: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.55,
    },
}));

/**
 * The groups page: start a new group, then every group as a card, mine and
 * the ones I was invited to, with the secondary actions (requests, edit,
 * share, delete for mine; past activity for theirs) in a per-card menu.
 */
const ManageClass = ({ allowedGroups, profile }) => {
    const classes = useStyles();
    const history = useHistory();
    const { dataAll, isLoading, isError, loadClasses, setIsLoading } = loadClassesHook(allowedGroups);
    const [onlyMine, setOnlyMine] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const groups = onlyMine ? dataAll.filter((row) => row.isMine) : dataAll;

    const lessonStart = (classId) => {
        window.logAppActivity(GA_EVENT_TREE.manageGroups.actions.start);
        history.push('/lesson/' + classId);
    };

    const deleteGroup = () => {
        window.logAppActivity(GA_EVENT_TREE.manageGroups.actions.delete);
        setIsLoading(true);
        const { id } = deleting;
        setDeleting(null);
        classApi
            .deleteClass(id)
            .then(() => loadClasses())
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
    };

    const actionsFor = (row) => {
        if (!row.isMine) {
            return [
                {
                    key: 'view',
                    labelId: 'redesign.generic.label.view',
                    onClick: () =>
                        store.dispatch(
                            showMultiBoardLessonState({
                                id: null,
                                lessonId: row.classId,
                                name: row.name,
                                email: profile.email,
                                isOwner: false,
                            })
                        ),
                },
            ];
        }
        const requests = Number(row.requests) || 0;
        return [
            requests > 0 && {
                key: 'requests',
                labelId: 'redesign.generic.label.newRequests',
                badge: requests,
                onClick: () => {
                    window.logAppActivity(GA_EVENT_TREE.manageGroups.actions.requests);
                    store.dispatch(userJoinRequest({ classId: row.id, userId: null }));
                },
            },
            {
                key: 'edit',
                labelId: 'redesign.generic.label.edit',
                icon: EditIcon,
                onClick: () => {
                    window.logAppActivity(GA_EVENT_TREE.manageGroups.actions.edit);
                    history.push('/class/' + row.id);
                },
            },
            {
                key: 'share',
                labelId: 'generic.label.shareLesson',
                icon: ShareLessonIcon,
                onClick: () => {
                    window.logAppActivity(GA_EVENT_TREE.editGroup.actions.share);
                    store.dispatch(shareLesson({ id: row.id, name: row.name }));
                },
            },
            {
                key: 'delete',
                labelId: 'redesign.generic.label.delete',
                icon: DeleteIcon,
                danger: true,
                onClick: () => setDeleting(row),
            },
        ].filter(Boolean);
    };

    return (
        <Grid item xs={12}>
            <LandingSection id="top" background="none" gutter={0} paddingTop={8} paddingBottom={24}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="page.home.feature.groups.tag" />
                        </SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>
                        <FormattedMessage id="redesign.groups.title" />
                    </h1>
                    <p className={classes.lead}>
                        <FormattedMessage id="redesign.groups.lead" />
                    </p>
                </div>
                <CreateGroupForm />
            </LandingSection>

            <LandingSection background="none" gutter={0} paddingTop={0} paddingBottom={56}>
                <div className={classes.toolbar}>
                    <h2 className={classes.count}>
                        {isLoading ? '…' : <FormattedMessage id="redesign.groups.count" values={{ count: groups.length }} />}
                    </h2>
                    <div className={classes.controls}>
                        <div className={classes.track} role="group">
                            <button
                                type="button"
                                aria-pressed={!onlyMine}
                                onClick={() => setOnlyMine(false)}
                                className={cx(classes.toggle, !onlyMine && classes.toggleOn)}
                            >
                                <FormattedMessage id="redesign.groups.filter.all" />
                            </button>
                            <button
                                type="button"
                                aria-pressed={onlyMine}
                                onClick={() => setOnlyMine(true)}
                                className={cx(classes.toggle, onlyMine && classes.toggleOn)}
                            >
                                <FormattedMessage id="redesign.groups.filter.mine" />
                            </button>
                        </div>
                        <Link to={tutorialLinks.groups} className={classes.howItWorks}>
                            <FormattedMessage id="redesign.groups.howItWorks" />
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                </div>

                {isLoading ? <GenericState isLoading={true} inline={true} /> : null}
                {isError ? <GenericState isError={true} inline={true} /> : null}

                {!isLoading && !isError && groups.length ? (
                    <GroupList groups={groups} profile={profile} onStart={lessonStart} actionsFor={actionsFor} />
                ) : null}

                {!isLoading && !isError && !groups.length ? (
                    <div className={classes.empty}>
                        <h3 className={classes.emptyTitle}>
                            <FormattedMessage id="redesign.home.dashboard.recent.emptyTitle" />
                        </h3>
                        <p className={classes.emptyText}>
                            <FormattedMessage
                                id={
                                    onlyMine && dataAll.length
                                        ? 'redesign.groups.emptyMine'
                                        : 'redesign.home.dashboard.recent.emptyText'
                                }
                            />
                        </p>
                    </div>
                ) : null}
            </LandingSection>

            <GenericDeletionConfirmation
                open={!!deleting}
                deleteFunction={deleteGroup}
                onClose={() => setDeleting(null)}
                value={deleting ? deleting.name : ''}
            />
        </Grid>
    );
};

const mapStateToProps = (state) => {
    const { profile } = state;
    return { allowedGroups: profile.allowedGroups, profile };
};

export default connect(mapStateToProps)(ManageClass);
